/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { ToggleCard } from '@/components/toggle-card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type { CheckoutActionData } from '../types';
import type { ShopperBasketsV2 } from '@/scapi';
import { useTranslation } from 'react-i18next';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { formatCurrency } from '@/lib/currency';
import { useShippingOptions } from './use-shipping-options';
import { cn } from '@/lib/utils';

interface ShippingOptionsProps {
    onSubmit: (formData: FormData) => void;
    isLoading: boolean;
    actionData?: CheckoutActionData;
    shippingMethods?: ShopperBasketsV2.schemas['ShippingMethodResult'];
    validationError?: string | null;
    isCompleted: boolean;
    isEditing: boolean;
    onEdit: () => void;
}

export default function ShippingOptions({
    onSubmit,
    isLoading,
    actionData,
    shippingMethods,
    validationError: _validationError,
    isCompleted,
    isEditing,
    onEdit: _onEdit,
}: ShippingOptionsProps) {
    const { currency } = useSite();
    const { t, i18n } = useTranslation('checkout');

    const { availableShippingMethods, selectedMethod, defaultShippingMethodId, getDiscountedPrice } =
        useShippingOptions({ onSubmit, isLoading, actionData, shippingMethods, isEditing });

    // A step the shopper hasn't reached (or completed) yet — not merely "no methods loaded" —
    // is what makes this card render as the disabled/upcoming-step title-only container.
    const isUpcomingStep = !isCompleted && !isEditing;

    const stepTitle = (
        <span
            className={cn(
                'text-2xl font-bold tracking-tight',
                isCompleted && !isEditing ? 'text-[#3F3F46]' : 'text-[color:var(--checkout-step-upcoming)]'
            )}>
            {t('shippingOptions.title', { defaultValue: 'Shipping Method' })}
        </span>
    );

    return (
        <ToggleCard
            id="shipping-options"
            title={stepTitle}
            editing={isEditing}
            disabled={isUpcomingStep}
            hideContent={isUpcomingStep}
            showHeaderSeparator
            isLoading={isLoading}>
            <div className="flex flex-col gap-6 pt-2 pb-2">
                <RadioGroup
                    name="shippingMethodId"
                    value={selectedMethod?.id || defaultShippingMethodId || ''}
                    onValueChange={(value) => {
                        if (isLoading) return;
                        const formData = new FormData();
                        formData.append('shippingMethodId', value);
                        onSubmit(formData);
                    }}
                    required
                    aria-label={t('shippingOptions.title')}
                    className="grid grid-cols-1 gap-[10px] md:grid-cols-2">
                    {availableShippingMethods.map((method) => {
                        const isSelected = (selectedMethod?.id || defaultShippingMethodId) === method.id;
                        const discountedPrice = getDiscountedPrice(method.price);
                        const isFree = discountedPrice === 0;
                        const hasPromo = Boolean(method.shippingPromotions?.length) && method.price > 0;

                        return (
                            <label
                                key={method.id}
                                htmlFor={method.id}
                                className={`flex h-[61px] w-full cursor-pointer items-start gap-[10px] rounded-[4px] border bg-white p-3 transition-all duration-200 md:w-[330.5px] ${
                                    isSelected
                                        ? 'border-black'
                                        : 'border-[#D1D5DB] hover:border-[#9CA3AF]'
                                }`}>
                                <RadioGroupItem
                                    value={method.id}
                                    id={method.id}
                                    className="mt-0.5 shrink-0 border-[#9CA3AF] data-[state=checked]:border-2"
                                    autoFocus={isEditing && availableShippingMethods.indexOf(method) === 0}
                                />
                                <div className="flex min-w-0 flex-col">
                                    <span className="truncate text-sm font-medium leading-snug text-[#17171B]">
                                        {method.name}
                                    </span>
                                    <span className="truncate text-xs text-[#71717A]">
                                        {hasPromo && (
                                            <span className="mr-1 text-muted-foreground line-through">
                                                {formatCurrency(method.price, i18n.language, currency)}
                                            </span>
                                        )}
                                        {isFree
                                            ? t('shippingOptions.free')
                                            : formatCurrency(discountedPrice, i18n.language, currency)}
                                    </span>
                                </div>
                            </label>
                        );
                    })}
                </RadioGroup>
            </div>
        </ToggleCard>
    );
}
